import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useReadRmaDetail from "../hooks/useReadRmaDetail";
import EmptyTableRow from "../../../common/EmptyTableRow";
import { SubmitHandler, useForm, useWatch } from "react-hook-form";
import { RmaDetailType, StdType } from "../../../../types/support";
import moment from "moment";
import { useModal } from "../../../../hooks";
import SearchStndInner from "../../../../components/common/modal/inner/SearchStndInner";
import useCreateRmaCode from "../hooks/useCreateRmaCode";
import { SearchContactsInner } from "../../../../components/common/modal/inner/SearchContactsInner";
import { SearchContactType } from "../../../customer/contact/interface";
import { SearchSystemUserInner } from "../../../../components/common/modal/inner/SearchSystemUserInner";
import { SystemUserType } from "../../../customer/correspondent/detail/interface";
import useUpdateRma from "../hooks/useUpdateRma";
import { useReadHandlerData } from "../hooks/useReadHandlerData";
import { useReadUserData } from "../hooks/useReadUserData";
import useUploadRmaExcel from "../hooks/useUploadRmaExcel";
import useCreateRma from "../hooks/useCreateRma";
import useVerifySerialNumber from "../hooks/useVerifySerialNumber";
import { getJwtPayloadValue } from "../../../../util/getJwtPayloadValue";
import { RmaHandlerType, RmaUserDataType } from '../../../../types/support/index';
import { SearchUserDataInner } from "../../../../components/common/modal/inner/SearchUserDataInner";
import { number } from "zod";
import { useUser } from "../../../../util/userContext/UserContext";
import { UserInterface } from '../../../../util/userContext/interface/UserInterface';

const DefaultData: RmaDetailType = {
    rmaId: 0,
    code: "",
    rmaAt: moment(new Date()).format("YYYY-MM-DD"),
    newTxtStndItem: "",
    coa: "",
    quantity: 0,
    questionState: "",
    distributorName: "",
    msSoldToNumber: "",
    agreementNumber: "",
    contactInformation: "",
    customerCompanyName: "",
    vendor: "",
    category: "",
    newTxtNmItem: "",
    customerAlias: "",
    customerPhoneNo: "",
    customerCoEmail: "",
    handlerName: "",
    handlerId: "",
    requesterId: "",
    rmaDetailList: [],
    requesterName: "",
    customerId: "",
};

export const RmaDetailInput = () => {
    const navigate = useNavigate();
    const { handleModal } = useModal();
    const [tab, setTab] = useState("tab-1");
    const { id } = useParams();
    const { data } = useReadRmaDetail(id as string);
    const fileRef = useRef<HTMLInputElement>(null);
    const { user } = useUser();

    const { data: rmaCode } = useCreateRmaCode();
    const { data: handlerData } = useReadHandlerData(); // ✅ 추가된 부분

    const { getValues, register, reset, setValue, handleSubmit, control } =
        useForm<RmaDetailType>({
            defaultValues: { ...DefaultData, code: rmaCode },
        });

    const { mutate: createRma } = useCreateRma();
    const { mutate: updateRma } = useUpdateRma();
    const { mutate: uploadMutate } = useUploadRmaExcel({ setValue });
    const { mutate: mutateVerifySerialNumber } = useVerifySerialNumber();

    useEffect(() => {
        if (id && data?.rmaDetail) {
            reset(data.rmaDetail)
            if (getJwtPayloadValue('rmaWriteYn') === false) {
                setValue("customerAlias", user?.userName ?? '');
                setValue("customerCoEmail", user?.coEmail ?? '');
                setValue("customerPhoneNo", user?.phoneNo ?? '');
                setValue("customerId", String(user?.userId) ?? '');
            }
        };
    }, [data]);

    useEffect(() => {
        if (id) {
        } else {
            if (rmaCode) {
                reset({ ...getValues, code: rmaCode });
                if (getJwtPayloadValue('rmaWriteYn') === false) {
                    setValue("customerAlias", user?.userName ?? '');
                    setValue("customerCoEmail", user?.coEmail ?? '');
                    setValue("customerPhoneNo", user?.phoneNo ?? '');
                    setValue("customerId", String(user?.userId) ?? '');
                }
            }
        }
    }, [rmaCode]);

    useEffect(() => {
        if (user && getJwtPayloadValue('rmaWriteYn') === false) {
            setValue("customerAlias", user.userName);
            setValue("customerCoEmail", user.coEmail);
            setValue("customerPhoneNo", user.phoneNo);
            setValue("customerId", String(user.userId));
        }
    }, [user]);

    const watches = {
        ...DefaultData,
        ...useWatch<RmaDetailType>({ control }),
    };

    const onSubmit: SubmitHandler<RmaDetailType> = (submitData) => {
        const { rmaDetailList, ...res } = submitData;
        if (id) {
            updateRma({
                ...res,
                updateRmaDetailReqList: rmaDetailList ? rmaDetailList : [],
            });
        } else {
            createRma({
                ...res,
                createRmaDetailReqList: rmaDetailList ? rmaDetailList : [],
            });
        }
    };

    const handleButtonClick = () => {
        if (fileRef?.current) {
            fileRef.current.click();
        }
    };

    const handleChange = () => {
        if (fileRef?.current) {
            const data = fileRef.current.files;

            if (data) {
                const form = new FormData();
                form.append("excelFile", data?.[0]);
                uploadMutate(form);
            }
        }
    };

    const verifySerialNo = () => {
        const list = getValues("rmaDetailList") ?? []

        if (list.length === 0) {
            handleModal({
                isOpen: true,
                contents: "검증할 항목이 없습니다.",
                type: "alert"
            })
        } else {
            mutateVerifySerialNumber(list)
        }
    }

    return (
        <>
            <div className="content_wrap">
                <div className="content_top">
                    <h3 className="filter_heading">RMA 등록/수정</h3>
                    <div className=""></div>
                    <div className="content_toolbar">
                        <button
                            className="btn btn-primary w-100px h-38px"
                            onClick={handleSubmit(onSubmit)}
                        >
                            저장
                        </button>
                        <button
                            className="btn btn-outline-secondary w-100px h-38px"
                            onClick={() =>
                                data?.rmaDetail.rmaId
                                    ? navigate(
                                        `${process.env.PUBLIC_URL}/support/rma/detail/${data.rmaDetail.rmaId}`,
                                        { replace: true }
                                    )
                                    : navigate(-1)
                            }
                        >
                            취소
                        </button>
                    </div>
                </div>
                <h5 className="subheading">RMA 기본정보</h5>

                <table className="table table-bordered my_table table_detail">
                    <tbody>
                        <tr>
                            <th style={{ width: "16.66%" }}>RMA 번호</th>
                            <td style={{ width: "16.66%" }}>{getValues("code")}</td>
                            <th style={{ width: "16.66%" }}>RMA 일자</th>
                            <td style={{ width: "16.66%" }} className="p-4px">
                                <input
                                    type="date"
                                    className="form-control"
                                    {...register("rmaAt")}
                                    disabled={true}
                                />
                            </td>
                            <th style={{ width: "16.66%" }}>규격 <span className="required">*</span></th>
                            <td className="p-4px">
                                <div className="d-flex justify-content-between align-items-center">
                                    <input
                                        type="text"
                                        className="form-control"
                                        disabled
                                        {...register("newTxtStndItem")}
                                    />
                                    <button
                                        className="btn btn-info btn_search ms-5px"
                                        onClick={() =>
                                            handleModal({
                                                isOpen: true,
                                                contents: (
                                                    <SearchStndInner
                                                        confirmCallback={(item: StdType) => {
                                                            // 규격
                                                            setValue("newTxtStndItem", item?.newTxtStndItem);
                                                            // 벤더
                                                            setValue("vendor", item?.vendor);
                                                            // 카테고리
                                                            setValue("category", item?.category);
                                                            // 품목명
                                                            setValue("newTxtNmItem", item?.newTxtNmItem);
                                                            console.log(item);
                                                        }}
                                                        modalDepth={1}
                                                    />
                                                ),
                                                type: "",
                                                title: "규격 조회",
                                                modalSize: "xl",
                                            })
                                        }
                                    >
                                        <img
                                            src={
                                                process.env.PUBLIC_URL + "/assets/images/icon_search.png"
                                            }
                                            alt=""
                                        />
                                        찾기
                                    </button>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <th>COA</th>
                            <td className="p-4px">
                                <input
                                    type="text"
                                    className="form-control"
                                    {...register("coa")}
                                />
                            </td>
                            <th>수량 <span className="required">*</span></th>
                            <td>
                                <div className="d-flex align-items-center">
                                    {getValues("quantity")}
                                    <div> EA</div>
                                </div>
                            </td>
                            <th>상태 <span className="required">*</span></th>
                            <td className="p-4px">
                                {getJwtPayloadValue("rmaWriteYn") ? <select className="form-select" {...register("questionState")}>
                                    <option value="접수">접수</option>
                                    <option value="요청">요청</option>
                                    <option value="진행">진행</option>
                                    <option value="종료">종료</option>
                                </select> :
                                    <select className="form-select" {...register("questionState")}>
                                        <option value="접수">접수</option>
                                    </select>}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="mybtns_group">
                <div className="btn-group" role="group" aria-label="">
                    <button
                        type="button"
                        className={
                            tab == "tab-1" ? "btn btn-light active" : "btn btn-light"
                        }
                        onClick={() => setTab("tab-1")}
                    >
                        RMA 정보
                    </button>
                    <button
                        type="button"
                        className={
                            tab == "tab-2" ? "btn btn-light active" : "btn btn-light"
                        }
                        onClick={() => setTab("tab-2")}
                    >
                        고객정보
                    </button>
                </div>
                <div className="mybtns_group_toolbar">
                    <label htmlFor="file-upload">
                        <input
                            type="file"
                            className="d-none"
                            ref={fileRef}
                            id="file-upload"
                            onChange={handleChange}
                        />
                        <button
                            className="btn btn-outline-dark w-150px px-0"
                            onClick={() => {
                                handleButtonClick();
                            }}
                        >
                            <img
                                src={process.env.PUBLIC_URL + "/assets/images/icon_import.png"}
                                alt=""
                            />
                            엑셀 가져오기
                        </button>
                    </label>
                </div>
            </div>

            {tab == "tab-1" && (
                <>
                    <div className="content_wrap">
                        <div className="table_scroll mb-0">
                            <table className="table table-bordered my_table table_detail">
                                <tbody>
                                    <tr>
                                        <th style={{ width: "16.66%" }}>Distributor Name</th>
                                        <td colSpan={3}>{getValues("distributorName")}</td>
                                    </tr>
                                    <tr>
                                        <th>MS Sold to Number</th>
                                        <td>{getValues("msSoldToNumber")}</td>
                                        <th style={{ width: "16.66%" }}>Agreement Number</th>
                                        <td>{getValues("agreementNumber")}</td>
                                    </tr>
                                    <tr>
                                        <th>Contact Information</th>
                                        <td colSpan={3}>{getValues("contactInformation")}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="table_top mt-15px">
                        <h6 className="table_stats">
                        </h6>
                        <div className="table_toolbar">
                            {
                                getJwtPayloadValue("rmaWriteYn") &&
                                <button className="btn btn-dark" onClick={verifySerialNo}>시리얼 번호 검증</button>
                            }
                        </div>
                    </div>
                    <div className="content_wrap">
                        <div className="table_scroll m-0">
                            <table className="table table-bordered my_table">
                                <thead>
                                    <tr>
                                        <th style={{ width: "4.4%" }}>No</th>
                                        <th style={{ width: "10.56%" }}>End Item Number</th>
                                        <th style={{ width: "10.56%" }}>COA Base Stock</th>
                                        <th style={{ width: "13.64%" }}>Beginning COA Number *</th>
                                        <th style={{ width: "11%" }}>Ending COA Number</th>
                                        <th style={{ width: "7.5%" }}>Quantity</th>
                                        <th style={{ width: "10.56%" }}>Original PO</th>
                                        <th style={{ width: "10.56%" }}>Return Reason</th>
                                        <th style={{ width: "10.56%" }}>Comments</th>
                                        <th>Received Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {watches.rmaDetailList?.length === 0 ? (
                                        <EmptyTableRow colSpan={10} msg={"항목이 없습니다."} />
                                    ) : (
                                        watches.rmaDetailList?.map((item, index) => (
                                            <tr>
                                                <td className="text-center">{index + 1}</td>
                                                {/* 규격 */}
                                                <td className="text-center">{item.endItemNumber}</td>
                                                <td className="text-center">{item.coaBaseStock}</td>
                                                <td className="text-center">
                                                    {item.beginningCoaNumber}
                                                </td>
                                                <td className="text-center">{item.endingCoaNumber}</td>
                                                <td className="text-center">{item.quantity}</td>
                                                <td className="text-center">{item.originalPo}</td>
                                                <td className="text-center">{item.returnReason}</td>
                                                <td className="text-center">{item.comments}</td>
                                                <td className="text-center">{item.receivedDate}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            {tab == "tab-2" && (
                <div className="content_wrap">
                    <div className="table_scroll m-0">
                        <table className="table table-bordered my_table table_detail">
                            <tbody>
                                <tr>
                                    <th style={{ width: "16.7%" }}>거래처</th>
                                    <td style={{ width: "33.33%" }}>
                                        {getValues("customerCompanyName")}
                                    </td>
                                    <th style={{ width: "8.3%" }} rowSpan={3}>
                                        연락처
                                    </th>
                                    <th style={{ width: "8.3%" }}>성함 <span className="required">*</span></th>
                                    <td className="p-4px">
                                        {/* {data?.rmaDetail.customerAlias} */}
                                        {
                                            getJwtPayloadValue("rmaWriteYn") ?
                                                <div className="d-flex">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        {...register("customerAlias")}
                                                        disabled
                                                    />
                                                    <button
                                                        className="btn btn-info btn_search ms-5px"
                                                        onClick={() =>
                                                            handleModal({
                                                                isOpen: true,
                                                                contents: (
                                                                    <SearchUserDataInner
                                                                        confirmCallback={(item: RmaUserDataType) => {
                                                                            console.log(item);
                                                                            setValue("customerAlias", item?.userName);
                                                                            setValue(
                                                                                "customerCoEmail",
                                                                                item?.loginId
                                                                            );
                                                                            setValue(
                                                                                "customerPhoneNo",
                                                                                item?.contactNo
                                                                            );
                                                                            setValue("customerId", String(item?.userId));
                                                                        }}
                                                                        modalDepth={1}
                                                                    />
                                                                ),
                                                                type: "",
                                                                title: "연락처 조회",
                                                                modalSize: "xxl",
                                                            })
                                                        }
                                                    >
                                                        <img
                                                            src={
                                                                process.env.PUBLIC_URL +
                                                                "/assets/images/icon_search.png"
                                                            }
                                                            alt=""
                                                        />
                                                        찾기
                                                    </button>

                                                    {/*
                                        <button
                                            className="btn btn-info btn_search ms-5px"
                                            onClick={() =>
                                                handleModal({
                                                    isOpen: true,
                                                    contents: (
                                                        <SearchContactsInner
                                                            confirmCallback={(item: SearchContactType) => {
                                                                console.log(item);
                                                                setValue("customerAlias", item?.fullName);
                                                                setValue(
                                                                    "customerCoEmail",
                                                                    item?.emailAddress1
                                                                );
                                                                setValue(
                                                                    "customerCompanyName",
                                                                    item?.companyName
                                                                );
                                                                setValue("customerId", item?.contactId);
                                                            }}
                                                            modalDepth={1}
                                                        />
                                                    ),
                                                    type: "",
                                                    title: "연락처 조회",
                                                    modalSize: "xxl",
                                                })
                                            }
                                        >
                                            <img
                                                src={
                                                    process.env.PUBLIC_URL +
                                                    "/assets/images/icon_search.png"
                                                }
                                                alt=""
                                            />
                                            찾기
                                        </button>
                                        * */}
                                                </div> : <div className="d-flex">
                                                    <span style={{ paddingLeft: '2.5px' }}>{user?.userName}</span>
                                                </div>
                                        }
                                    </td>
                                </tr>
                                <tr>
                                    {/* input */}
                                    <th>RMA 일자</th>
                                    <td>{getValues("rmaAt")}</td>
                                    <th>전화번호</th>
                                    {
                                        getJwtPayloadValue("rmaWriteYn") ?
                                            <td>{getValues("customerPhoneNo")}</td>
                                            :
                                            <td>{user?.phoneNo}</td>
                                    }

                                </tr>
                                <tr>
                                    <th>벤더</th>
                                    <td>{getValues("vendor")}</td>
                                    <th>이메일</th>
                                    {
                                        getJwtPayloadValue("rmaWriteYn") ?
                                            <td>{getValues("customerCoEmail")}</td>
                                            :
                                            <td>{user?.coEmail}</td>
                                    }
                                </tr>
                                <tr>
                                    <th>카테고리</th>
                                    <td>{getValues("category")}</td>
                                    <th colSpan={2}>
                                        담당자
                                    </th>
                                    <td className="p-4px">
                                        <div className="d-flex">

                                            <select className="form-select" {...register("handlerId")} disabled={!getJwtPayloadValue("rmaWriteYn")}
                                                onChange={(e) => {
                                                    const selectedHandler = handlerData?.find((handler: RmaHandlerType) => handler.userId === Number(e.target.value));
                                                    setValue("handlerId", e.target.value);
                                                    setValue("handlerName", selectedHandler?.userName || ""); // handlerName 업데이트
                                                }}
                                            >
                                                <option value="">담당자를 선택하세요</option>
                                                {handlerData?.map((handler: RmaHandlerType) => (
                                                    <option key={handler.userId} value={handler.userId}>
                                                        {handler.userName}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <th>품목명</th>
                                    <td>{getValues("newTxtNmItem")}</td>
                                    <th colSpan={2}>요청자</th>
                                    <td className="p-4px">
                                        <div className="d-flex">
                                            {/*
                                        <input
                                            type="text"
                                            className="form-control"
                                            {...register("requesterName")}
                                            disabled
                                        />
                                        <button
                                            className="btn btn-info btn_search ms-5px"
                                            onClick={() =>
                                                handleModal({
                                                    isOpen: true,
                                                    contents: (
                                                        <SearchSystemUserInner
                                                            confirmCallback={(item: SystemUserType) => {
                                                                console.log(item);
                                                                setValue("requesterId", item?.systemuserid);
                                                                setValue("requesterName", item?.fullname);
                                                            }}
                                                            modalDepth={1}
                                                        />
                                                    ),
                                                    type: "",
                                                    title: "요청자 조회",
                                                    modalSize: "xl",
                                                })
                                            }
                                            disabled={!getJwtPayloadValue("rmaWriteYn")}
                                        >
                                            <img
                                                src={
                                                    process.env.PUBLIC_URL +
                                                    "/assets/images/icon_search.png"
                                                }
                                                alt=""
                                            />
                                            찾기
                                        </button>
                                        * */}

                                            <select className="form-select" {...register("requesterId")} disabled={!getJwtPayloadValue("rmaWriteYn")}
                                                onChange={(e) => {
                                                    const selectedHandler = handlerData?.find((handler: RmaHandlerType) => handler.userId === Number(e.target.value));
                                                    setValue("requesterId", e.target.value);
                                                    setValue("requesterName", selectedHandler?.userName || ""); // handlerName 업데이트
                                                }}
                                            >
                                                <option value="">담당자를 선택하세요</option>
                                                {handlerData?.map((handler: RmaHandlerType) => (
                                                    <option key={handler.userId} value={handler.userId}>
                                                        {handler.userName}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <th>규격</th>
                                    {/* 위에있는거 */}
                                    <td>{getValues("newTxtStndItem")}</td>
                                    <th colSpan={2}>수량</th>
                                    {/* 위에있는거 */}
                                    <td colSpan={2}>{watches?.quantity}</td>
                                </tr>
                                <tr>
                                    {/* 위에있는 coa */}
                                    <th>COA</th>
                                    <td colSpan={5}>{watches?.coa}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </>
    );
};
